import React, { useEffect, useMemo, useState } from 'react';
import { Plus, RefreshCw, Search, Pencil, Trash2, ChevronUp, ChevronDown, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { adminAPI } from '../../services/api';
import Toast from '../../components/admin/Toast';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import GTMFormModal from '../../components/admin/GTMFormModal';
import '../../styles/AdminGlobal.css';
import '../../styles/AdminShared.css';
import './Admin.css';

interface GTMConfig {
  id?: number;
  container_id: string;
  container_name: string;
  is_active: boolean;
  environment: string;
  environment_display?: string;
  auth_token: string;
  preview_token: string;
  enable_enhanced_ecommerce: boolean;
  created_at?: string;
  updated_at?: string;
}

const GTMManagement: React.FC = () => {
  const [configs, setConfigs] = useState<GTMConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('id');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<GTMConfig | null>(null);
  const [toast, setToast] = useState<{show: boolean, message: string, type: 'success'|'error'}>({ show: false, message: '', type: 'success' });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  useEffect(() => { loadConfigs(); }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getGTM();
      let data = response.data || response;
      if (data?.results && Array.isArray(data.results)) data = data.results;
      setConfigs(Array.isArray(data) ? data : data ? [data] : []);
    } catch (error) { console.error('Failed:', error); setConfigs([]); }
    finally { setLoading(false); }
  };

  const handleAdd = () => { setSelectedConfig(null); setIsModalOpen(true); };
  const handleEdit = (c: GTMConfig) => { setSelectedConfig(c); setIsModalOpen(true); };
  const handleDeleteClick = (c: GTMConfig) => { setSelectedConfig(c); setIsDeleteDialogOpen(true); };

  const handleDelete = async () => {
    if (!selectedConfig) return;
    try {
      await adminAPI.deleteGTM(selectedConfig.id!);
      setToast({ show: true, message: 'Config deleted', type: 'success' });
      loadConfigs();
    } catch { setToast({ show: true, message: 'Failed to delete', type: 'error' }); }
    finally { setIsDeleteDialogOpen(false); setSelectedConfig(null); }
  };

  const handleSave = async (data: any) => {
    try {
      if (selectedConfig?.id) { await adminAPI.updateGTM(selectedConfig.id, data); setToast({ show: true, message: 'Config updated', type: 'success' }); }
      else { await adminAPI.createGTM(data); setToast({ show: true, message: 'Config created', type: 'success' }); }
      localStorage.removeItem('gtm_config');
      setIsModalOpen(false); setSelectedConfig(null); loadConfigs();
    } catch (error: any) {
      setToast({ show: true, message: error.response?.data?.message || 'Failed to save', type: 'error' });
    }
  };

  const handleSort = (f: string) => { if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortField(f); setSortDir('asc'); } setCurrentPage(1); };
  const SA = ({ field }: { field: string }) => sortField !== field ? null : sortDir === 'asc' ? <ChevronUp size={10} className="adm-sort-arrow" /> : <ChevronDown size={10} className="adm-sort-arrow" />;

  const envChip = (env: string) => {
    if (env === 'live') return 'adm-chip-green';
    if (env === 'latest') return 'adm-chip-amber';
    return 'adm-chip-purple';
  };

  const filtered = useMemo(() => {
    let list = configs.filter(c => [c.container_id, c.container_name].some(v => v?.toLowerCase().includes(searchTerm.toLowerCase())));
    list = [...list].sort((a: any, b: any) => sortDir === 'asc' ? String(a[sortField] ?? '').localeCompare(String(b[sortField] ?? '')) : String(b[sortField] ?? '').localeCompare(String(a[sortField] ?? '')));
    return list;
  }, [configs, searchTerm, sortField, sortDir]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="admin-page adm-page">
      <div className="adm-topbar">
        <h1 className="adm-page-title">Google Tag Manager</h1>
        <div className="adm-topbar-right">
          <button className="adm-btn adm-btn-add" onClick={handleAdd}><Plus size={13} /> Add Config <span className="adm-btn-badge">{configs.length}</span></button>
          
        </div>
      </div>
      <div className="adm-filter-wrap">
        <div className="adm-filter-left"><span className="adm-count"><strong>{filtered.length}</strong> config(s)</span></div>
        <div className="adm-filter-right">
          <div className="adm-search-wrap">
            <span className="adm-search-icon"><Search size={13} /></span>
            <input className="adm-search-input" placeholder="Search..." value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
          </div>
        </div>
      </div>
      <div className="adm-table-card">
        <div className="adm-table-scroll">
          <table className="adm-table">
            <thead>
              <tr>
                <th className="adm-th adm-th-sort" onClick={() => handleSort('container_id')}>Container ID <SA field="container_id" /></th>
                <th className="adm-th adm-th-sort" onClick={() => handleSort('container_name')}>Name <SA field="container_name" /></th>
                <th className="adm-th">Environment</th>
                <th className="adm-th">Enhanced E-Commerce</th>
                <th className="adm-th adm-th-sort" onClick={() => handleSort('is_active')}>Status <SA field="is_active" /></th>
                <th className="adm-th" style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="adm-loading"><span className="adm-spin" style={{ marginRight: 8 }}><Loader2 size={18} /></span>Loading...</td></tr>}
              {!loading && paginated.length === 0 && <tr><td colSpan={6} className="adm-empty">No GTM configs found.</td></tr>}
              {!loading && paginated.map((c: GTMConfig) => (
                <tr className="adm-row" key={c.id}>
                  <td className="adm-td"><span className="adm-mono">{c.container_id}</span></td>
                  <td className="adm-td"><span className="adm-name-main">{c.container_name}</span></td>
                  <td className="adm-td"><span className={'adm-chip ' + envChip(c.environment)}>{c.environment_display || c.environment}</span></td>
                  <td className="adm-td"><span className={'adm-chip ' + (c.enable_enhanced_ecommerce ? 'adm-chip-blue' : 'adm-chip-gray')}>{c.enable_enhanced_ecommerce ? 'Enabled' : 'Disabled'}</span></td>
                  <td className="adm-td"><span className={'adm-chip ' + (c.is_active ? 'adm-chip-green' : 'adm-chip-red')}>{c.is_active ? <><CheckCircle2 size={9} /> Active</> : <><XCircle size={9} /> Inactive</>}</span></td>
                  <td className="adm-td" style={{ padding: '6px 4px' }}>
                    <div className="adm-actions-row">
                      <button className="adm-action-btn adm-ab-edit" onClick={() => handleEdit(c)} title="Edit"><Pencil size={12} /></button>
                      <button className="adm-action-btn adm-ab-del" onClick={() => handleDeleteClick(c)} title="Delete"><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="adm-pagination">
          <div className="adm-page-size-wrap"><span>Show</span><select className="adm-page-size-select" value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>{[10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}</select><span>per page</span></div>
          <div className="adm-page-info">{filtered.length === 0 ? '0 configs' : ((currentPage - 1) * pageSize + 1) + '-' + Math.min(currentPage * pageSize, filtered.length) + ' of ' + filtered.length}</div>
          <div className="adm-page-controls">
            <button className="adm-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>&lsaquo;</button>
            <button className="adm-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>&rsaquo;</button>
          </div>
        </div>
      </div>
      <GTMFormModal isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); setSelectedConfig(null); }} onSave={handleSave} config={selectedConfig} />
      <ConfirmDialog isOpen={isDeleteDialogOpen} title="Delete GTM Config" message={'Delete this configuration?'} onConfirm={handleDelete} onClose={() => setIsDeleteDialogOpen(false)} confirmText="Delete" cancelText="Cancel" type="danger" />
      <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
    </div>
  );
};

export default GTMManagement;